import * as THREE from 'three';

/**
 * Procedural mesh primitives for the viewmodel.
 *
 * The single most important thing here is `chamferBox`. A hard 90-degree edge is the
 * loudest possible tell that a mesh was made by a programmer: real machined parts
 * always have a break-edge, and that tiny bevel is what catches a specular highlight
 * and draws the silhouette. Every box in the weapon models goes through it.
 *
 * Everything is generated non-indexed with flat normals, which is correct for
 * machined metal — you want each facet to read separately — and makes merging
 * trivial (`mergeGeom` is a plain array concatenation).
 *
 * UVs are box-projected per triangle at a fixed world texel density, so the forge's
 * bead-blast and stipple patterns stay the same physical size across every part
 * regardless of how big that part is.
 */

const DEFAULT_UV_SCALE = 0.055; // metres per texture tile

/* -------------------------------------------------------------------------- */
/* core                                                                        */
/* -------------------------------------------------------------------------- */

/** Raw triangle soup builder. Accumulates positions, then derives normals + UVs. */
export class MeshBuilder {
  constructor(uvScale = DEFAULT_UV_SCALE) {
    this.pos = [];
    this.uvScale = uvScale;
  }

  tri(a, b, c) {
    this.pos.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
    return this;
  }

  /** Quad as two triangles, wound a-b-c-d. */
  quad(a, b, c, d) {
    return this.tri(a, b, c).tri(a, c, d);
  }

  /** Emit a triangle, flipping winding if its normal points away from `ref`. */
  triFacing(a, b, c, ref) {
    const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2];
    const vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2];
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    const cx = (a[0] + b[0] + c[0]) / 3 - ref[0];
    const cy = (a[1] + b[1] + c[1]) / 3 - ref[1];
    const cz = (a[2] + b[2] + c[2]) / 3 - ref[2];
    if (nx * cx + ny * cy + nz * cz < 0) return this.tri(a, c, b);
    return this.tri(a, b, c);
  }

  quadFacing(a, b, c, d, ref) {
    return this.triFacing(a, b, c, ref).triFacing(a, c, d, ref);
  }

  build() {
    const g = new THREE.BufferGeometry();
    const p = new Float32Array(this.pos);
    g.setAttribute('position', new THREE.BufferAttribute(p, 3));
    g.computeVertexNormals(); // non-indexed -> flat, exactly what we want
    boxProjectUVs(g, this.uvScale);
    return g;
  }
}

/**
 * Box-project UVs from the dominant axis of each triangle's normal. Cheap, seam-heavy
 * at 45-degree faces, and completely invisible on a noise-based material — which is
 * all the forge produces.
 */
export function boxProjectUVs(geom, scale = DEFAULT_UV_SCALE) {
  const pos = geom.attributes.position.array;
  const nrm = geom.attributes.normal.array;
  const n = pos.length / 3;
  const uv = new Float32Array(n * 2);
  const inv = 1 / scale;
  for (let t = 0; t < n; t += 3) {
    // One axis choice per triangle keeps the face continuous.
    const ax = Math.abs(nrm[t * 3]);
    const ay = Math.abs(nrm[t * 3 + 1]);
    const az = Math.abs(nrm[t * 3 + 2]);
    let i0 = 0;
    let i1 = 1;
    if (ax >= ay && ax >= az) {
      i0 = 2;
      i1 = 1;
    } else if (ay >= az) {
      i0 = 0;
      i1 = 2;
    }
    for (let k = 0; k < 3; k++) {
      const v = (t + k) * 3;
      uv[(t + k) * 2] = pos[v + i0] * inv;
      uv[(t + k) * 2 + 1] = pos[v + i1] * inv;
    }
  }
  geom.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  return geom;
}

/** Concatenate non-indexed geometries that all carry position/normal/uv. */
export function mergeGeom(geoms) {
  let total = 0;
  for (const g of geoms) total += g.attributes.position.count;
  const pos = new Float32Array(total * 3);
  const nrm = new Float32Array(total * 3);
  const uv = new Float32Array(total * 2);
  let o3 = 0;
  let o2 = 0;
  for (const g of geoms) {
    pos.set(g.attributes.position.array, o3);
    nrm.set(g.attributes.normal.array, o3);
    uv.set(g.attributes.uv.array, o2);
    o3 += g.attributes.position.array.length;
    o2 += g.attributes.uv.array.length;
    g.dispose();
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
  out.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  return out;
}

/* -------------------------------------------------------------------------- */
/* chamfered box                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Box with a 45-degree break on every edge and corner. 44 triangles.
 *
 *   6 face quads  = 12 tris
 *  12 edge quads  = 24 tris
 *   8 corner tris =  8 tris
 *
 * `c` is the chamfer width in metres. 0.6–1.2 mm is the real-world range for a
 * machined receiver; anything above ~2 mm starts to look like a soap bar.
 */
export function chamferBox(w, h, d, c = 0.0012, uvScale = DEFAULT_UV_SCALE) {
  const W = w / 2;
  const H = h / 2;
  const D = d / 2;
  c = Math.min(c, W * 0.49, H * 0.49, D * 0.49);
  const b = new MeshBuilder(uvScale);
  const ref = [0, 0, 0];

  // Per-corner triple: the vertex pushed out on X, on Y, and on Z.
  const P = (sx, sy, sz, axis) => [
    sx * (axis === 0 ? W : W - c),
    sy * (axis === 1 ? H : H - c),
    sz * (axis === 2 ? D : D - c),
  ];

  const S = [-1, 1];

  // --- 6 faces ---
  for (const axis of [0, 1, 2]) {
    for (const s of S) {
      const corners = [];
      for (const a of S) {
        for (const bb of S) {
          const sx = axis === 0 ? s : a;
          const sy = axis === 1 ? s : axis === 0 ? a : bb;
          const sz = axis === 2 ? s : bb;
          corners.push(P(sx, sy, sz, axis));
        }
      }
      // corners is in [--,-+,+-,++] order over the two free axes; reorder to a ring.
      b.quadFacing(corners[0], corners[1], corners[3], corners[2], ref);
    }
  }

  // --- 12 edges ---
  const EDGE_AXES = [
    [0, 1, 2],
    [1, 2, 0],
    [2, 0, 1],
  ];
  for (const [a0, a1, free] of EDGE_AXES) {
    for (const s0 of S) {
      for (const s1 of S) {
        const mk = (sf, axis) => {
          const sg = [0, 0, 0];
          sg[a0] = s0;
          sg[a1] = s1;
          sg[free] = sf;
          return P(sg[0], sg[1], sg[2], axis);
        };
        b.quadFacing(mk(-1, a0), mk(-1, a1), mk(1, a1), mk(1, a0), ref);
      }
    }
  }

  // --- 8 corners ---
  for (const sx of S) {
    for (const sy of S) {
      for (const sz of S) {
        b.triFacing(P(sx, sy, sz, 0), P(sx, sy, sz, 1), P(sx, sy, sz, 2), ref);
      }
    }
  }

  return b.build();
}

/**
 * Plain (unchamfered) box, 12 triangles. Reserved for details small enough that a
 * chamfer would be sub-pixel — rail teeth, stipple pads, knurling. Anything the eye
 * can resolve as a flat panel goes through `chamferBox` instead.
 */
export function plainBox(w, h, d, uvScale = DEFAULT_UV_SCALE) {
  const g = new THREE.BoxGeometry(w, h, d).toNonIndexed();
  boxProjectUVs(g, uvScale);
  return g;
}

/* -------------------------------------------------------------------------- */
/* lathed tube along Z                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Surface of revolution about the Z axis (the bore axis for every weapon here).
 * `profile` is [[radius, z], ...]. A radius of 0 closes the end; put a chamfer in
 * the profile itself rather than relying on a bevel modifier we don't have.
 */
export function tubeZ(profile, segments = 12, uvScale = DEFAULT_UV_SCALE) {
  const b = new MeshBuilder(uvScale);
  const step = (Math.PI * 2) / segments;
  for (let i = 0; i < segments; i++) {
    const a0 = i * step;
    const a1 = (i + 1) * step;
    const c0 = Math.cos(a0);
    const s0 = Math.sin(a0);
    const c1 = Math.cos(a1);
    const s1 = Math.sin(a1);
    for (let k = 0; k < profile.length - 1; k++) {
      const [r0, z0] = profile[k];
      const [r1, z1] = profile[k + 1];
      const A = [c0 * r0, s0 * r0, z0];
      const B = [c1 * r0, s1 * r0, z0];
      const C = [c1 * r1, s1 * r1, z1];
      const D = [c0 * r1, s0 * r1, z1];
      // Winding: outward-facing for a profile listed from -Z to +Z.
      if (r0 < 1e-6) b.tri(A, C, D);
      else if (r1 < 1e-6) b.tri(A, B, C);
      else b.quad(A, B, C, D);
    }
  }
  return b.build();
}

/** Solid cylinder along Z with chamfered ends. */
export function rodZ(radius, z0, z1, segments = 12, chamfer = 0.0009, uvScale) {
  const c = Math.min(chamfer, Math.abs(z1 - z0) * 0.4, radius * 0.6);
  return tubeZ(
    [
      [0, z0],
      [radius - c, z0],
      [radius, z0 + c],
      [radius, z1 - c],
      [radius - c, z1],
      [0, z1],
    ],
    segments,
    uvScale
  );
}

/** Hollow tube along Z (open both ends, walls both sides). Used for optics bodies. */
export function pipeZ(rOuter, rInner, z0, z1, segments = 14, uvScale) {
  const outer = tubeZ([[rOuter, z0], [rOuter, z1]], segments, uvScale);
  const inner = tubeZ([[rInner, z1], [rInner, z0]], segments, uvScale);
  const ring0 = tubeZ([[rInner, z0], [rOuter, z0]], segments, uvScale);
  const ring1 = tubeZ([[rOuter, z1], [rInner, z1]], segments, uvScale);
  return mergeGeom([outer, inner, ring0, ring1]);
}

/** Torus in the XY plane (ring axis = Z). Sling loops, trigger guards, sight rings. */
export function ringZ(major, minor, seg = 12, tube = 5, arc = Math.PI * 2, uvScale) {
  const b = new MeshBuilder(uvScale ?? DEFAULT_UV_SCALE);
  for (let i = 0; i < seg; i++) {
    const a0 = (i / seg) * arc;
    const a1 = ((i + 1) / seg) * arc;
    for (let j = 0; j < tube; j++) {
      const t0 = (j / tube) * Math.PI * 2;
      const t1 = ((j + 1) / tube) * Math.PI * 2;
      const pt = (a, t) => {
        const r = major + minor * Math.cos(t);
        return [Math.cos(a) * r, Math.sin(a) * r, minor * Math.sin(t)];
      };
      b.quad(pt(a0, t0), pt(a1, t0), pt(a1, t1), pt(a0, t1));
    }
  }
  return b.build();
}

/* -------------------------------------------------------------------------- */
/* composite details                                                           */
/* -------------------------------------------------------------------------- */

/**
 * MIL-STD-1913 Picatinny rail: a base plus a run of recoil-lug teeth with real slots
 * between them. This is by far the highest value-per-triangle detail on a modern
 * weapon — the eye reads the repeating slot pattern as "gun" instantly.
 */
export function picatinnyRail(length, width = 0.0212, z0 = 0, teethCount = null) {
  const parts = [];
  const baseH = 0.0035;
  parts.push(translate(chamferBox(width, baseH, length, 0.0008), 0, baseH / 2, z0 - length / 2));

  const n = teethCount ?? Math.max(3, Math.round(length / 0.0102));
  const pitch = length / n;
  const toothD = pitch * 0.56;
  for (let i = 0; i < n; i++) {
    const z = z0 - length + pitch * (i + 0.5);
    // Teeth are 4 mm features; a chamfer on them would not survive rasterisation.
    parts.push(translate(plainBox(width * 0.98, 0.0042, toothD), 0, baseH + 0.0021, z));
  }
  return mergeGeom(parts);
}

/**
 * M-LOK style slot row cut into a handguard panel: built as segments with gaps, so
 * the cutouts are genuine geometry rather than a texture trick.
 */
export function slottedPanel(width, thickness, length, slots = 4, slotFrac = 0.42) {
  const parts = [];
  const pitch = length / slots;
  const solid = pitch * (1 - slotFrac);
  const half = length / 2;
  for (let i = 0; i <= slots; i++) {
    let z = -half + i * pitch;
    if (i === 0) z += solid / 2;
    else if (i === slots) z -= solid / 2;
    parts.push(translate(chamferBox(width, thickness, solid, 0.0007), 0, 0, z));
  }
  // Two thin longitudinal spines tie the segments together.
  parts.push(translate(chamferBox(width * 0.22, thickness * 0.95, length, 0.0006), width * 0.34, 0, 0));
  parts.push(translate(chamferBox(width * 0.22, thickness * 0.95, length, 0.0006), -width * 0.34, 0, 0));
  return mergeGeom(parts);
}

/** Cross-hatched grip texture: a grid of tiny raised pyramidal-ish pads. */
export function gripTexture(w, h, cols = 3, rows = 5, pad = 0.0032, depth = 0.0011) {
  const parts = [];
  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      const x = (i / (cols - 1 || 1) - 0.5) * (w - pad);
      const y = (j / (rows - 1 || 1) - 0.5) * (h - pad);
      parts.push(translate(plainBox(pad, pad, depth * 2), x, y, 0));
    }
  }
  return mergeGeom(parts);
}

/* -------------------------------------------------------------------------- */
/* geometry transforms                                                         */
/* -------------------------------------------------------------------------- */

const _m = new THREE.Matrix4();
const _e = new THREE.Euler();

export function translate(g, x, y, z) {
  g.translate(x, y, z);
  return g;
}

export function rotate(g, rx, ry, rz) {
  _e.set(rx, ry, rz, 'XYZ');
  _m.makeRotationFromEuler(_e);
  g.applyMatrix4(_m);
  return g;
}

export function scaleG(g, sx, sy = sx, sz = sx) {
  g.scale(sx, sy, sz);
  return g;
}

/** rotate then translate, in that order — the common case. */
export function place(g, x, y, z, rx = 0, ry = 0, rz = 0) {
  if (rx || ry || rz) rotate(g, rx, ry, rz);
  return translate(g, x, y, z);
}

export function triCount(object3D) {
  let n = 0;
  object3D.traverse((o) => {
    if (o.isMesh && o.geometry) {
      const g = o.geometry;
      n += g.index ? g.index.count / 3 : g.attributes.position.count / 3;
    }
  });
  return Math.round(n);
}

/**
 * Forge material with a fallback, because the surface library is owned by another
 * module and a missing id must not take the whole weapon layer down.
 */
const _fallbacks = new Map();
export function forgeMaterial(ctx, id, overrides) {
  try {
    return ctx.forge.material(id, overrides);
  } catch {
    const key = id + JSON.stringify(overrides ?? {});
    if (!_fallbacks.has(key)) {
      const tint = id === 'gunmetal' ? 0x2a2d31 : id === 'polymer' ? 0x1b1c1e : 0x3a3a3a;
      _fallbacks.set(
        key,
        new THREE.MeshStandardMaterial({
          color: tint,
          roughness: id === 'gunmetal' ? 0.42 : 0.62,
          metalness: id === 'gunmetal' ? 1 : 0,
          ...overrides,
        })
      );
    }
    return _fallbacks.get(key);
  }
}
